/**
 * BufferTimeline - Vertical timeline of buffer history
 *
 * Shows checkpoints, transforms, explorations, and adopt events
 * in a vertical timeline with icons and relative timestamps.
 *
 * @module @humanizer/studio/components/workspace/BufferTimeline
 */

import { useEffect, useState, useCallback } from 'react';

interface TimelineEvent {
  type: string;
  label: string;
  timestamp: number;
  wordCount: number;
  id: string;
}

interface BufferTimelineProps {
  bufferId: string;
  onRestore?: (eventId: string) => void;
  onClose?: () => void;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function eventIcon(type: string): string {
  if (type.startsWith('version:auto')) return '\u23F1';
  if (type.startsWith('version:manual')) return '\u2714';
  if (type.startsWith('version:transform')) return '\u21BB';
  if (type.startsWith('version:adopt')) return '\u2605';
  if (type.startsWith('version:restore')) return '\u21A9';
  if (type.startsWith('transform:')) return '\u2699';
  return '\u25CB';
}

export function BufferTimeline({ bufferId, onRestore, onClose }: BufferTimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!window.electronBuffer?.getTimeline) {
      setLoading(false);
      return;
    }
    setLoading(true);
    window.electronBuffer.getTimeline(bufferId)
      .then((result: TimelineEvent[]) => setEvents(result))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [bufferId]);

  const handleRestore = useCallback((eventId: string) => {
    onRestore?.(eventId);
  }, [onRestore]);

  if (loading) {
    return <div className="buffer-timeline__loading">Loading timeline...</div>;
  }

  if (events.length === 0) {
    return <div className="buffer-timeline__empty">No history yet</div>;
  }

  return (
    <div className="buffer-timeline">
      {onClose && (
        <div className="buffer-timeline__header">
          <span className="buffer-timeline__title">History</span>
          <button className="buffer-timeline__close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
      )}
      <div className="buffer-timeline__list">
        {events.map((event, i) => (
          <div key={`${event.id}-${i}`} className="buffer-timeline__event">
            <div className="buffer-timeline__connector">
              <span className="buffer-timeline__icon">{eventIcon(event.type)}</span>
              {i < events.length - 1 && <div className="buffer-timeline__line" />}
            </div>
            <div className="buffer-timeline__content">
              <div className="buffer-timeline__event-header">
                <span className="buffer-timeline__event-label">{event.label}</span>
                <span className="buffer-timeline__event-time">{relativeTime(event.timestamp)}</span>
              </div>
              {event.wordCount > 0 && (
                <span className="buffer-timeline__event-words">{event.wordCount.toLocaleString()} words</span>
              )}
              {onRestore && event.type.startsWith('version:') && (
                <button
                  className="buffer-timeline__restore-btn"
                  onClick={() => handleRestore(event.id)}
                >
                  Restore
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
